import * as React from "react";
import { useForm, Controller, useWatch, useFormContext as useOuterFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step6VehicleSchema, parseCurrencyToNumber } from "../../utils/schema";
import { ViewContext } from "components/lib";
import { classOptions, vehiclesBodytype } from "data";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../../components/ui/select";
import { Switch } from "../../../../components/ui/switch";
import { normalizeCurrencyInput } from "./utils";
import { createVehicle, updateVehicle } from "./api";
import { useEffect } from "react";
import { getVehicleData } from "../../utils/getVehicleData";
import { AlertCircle } from "lucide-react";

const HITCH_OPTIONS = [
  { value: "ball_at_bumper", label: "Ball at Bumper" },
  { value: "fifth_wheel", label: "5th Wheel" },
  { value: "tow_boom", label: "Tow Boom" },
  { value: "ball_in_bed", label: "Ball in Bed" },
  { value: "none", label: "None" },
];

export function VehicleUpsertDialog({
  open,
  onOpenChange,
  mode,               // "create" | "edit"
  initial,            // Vehicle in-form shape when editing
  companyId,
  onDone,             // (vehicle: Vehicle) => void
}) {
  const context = React.useContext(ViewContext);
  const [prevVin, setPrevVin] = React.useState(initial?.vin);
  const outer = useOuterFormContext();
  const carriersStep6 = useWatch({ control: outer.control, name: "step6.carrier" });
  const carriersStep1 = useWatch({ control: outer.control, name: "step1.carrier" });
  const carriers = (carriersStep6?.length ? carriersStep6 : carriersStep1) ?? [];
  const carriersNormalized = carriers.map((c) => c ?? "");
  const hasProgressive = carriersNormalized.includes("Progressive");
  const hasCoverWhale = carriersNormalized.includes("Cover Whale");
  const hasNirvana = carriersNormalized.includes("Nirvana");
  const hasCwOrNirvana = hasCoverWhale || hasNirvana;
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState(null);
  const [confirmMessage, setConfirmMessage] = React.useState("");
  const removeCwNirvanaCarriers = () => {
    const filtered = carriersNormalized.filter(
      (c) => c !== "Cover Whale" && c !== "Nirvana"
    );
    outer.setValue("step1.carrier", filtered, { shouldDirty: true, shouldValidate: true });
    outer.setValue("step6.carrier", filtered, { shouldDirty: true, shouldValidate: true });
    outer.setValue("step7.carrier", filtered, { shouldDirty: true, shouldValidate: true });
  };

  const form = useForm({
    resolver: zodResolver(Step6VehicleSchema),
    defaultValues:
      mode === "edit" && initial
        ? {
            id: initial.id,
            type: initial.type ?? "vehicle",
            year: initial.year,
            make: initial.make,
            model: initial.model,
            vin: initial.vin,
            value: initial.value,
            bodyType: initial.bodyType,
            class: initial.class,
            includeAPDTowing: !!initial.includeAPDTowing,
            deductibleType: initial.deductibleType ?? "FollowsPolicy",
            deductible: initial.deductible ?? "2500",
            hitchType: initial.hitchType ?? undefined,
          }
        : {
            type: "vehicle",
            year: "",
            make: "",
            model: "",
            vin: "",
            value: "",
            bodyType: undefined,
            class: undefined,
            includeAPDTowing: false,
            deductibleType: "FollowsPolicy",
            deductible: "2500",
            hitchType: undefined,
          },
    mode: "all",
  });

  const { control, handleSubmit, formState: { isSubmitting }, watch } = form;

  const values = watch();
  const vin = useWatch({ name: "vin", control, defaultValue: "" });

  const performSave = async (values) => {
    const payload = {
      type: "vehicle",
      company_id: companyId,
      year: values.year,
      make: values.make,
      model: values.model,
      vin: values.vin,
      stated_value: String(parseCurrencyToNumber(values.value)),
      bodytype: values.bodyType,
      class: values.class,
      includeAPDTowing: !!values.includeAPDTowing,
      hitchType: values.hitchType,
    };

    if (mode === "create") {
      const created = await createVehicle(payload);
      const normalized = {
        id: created.id,
        type: "vehicle",
        year: values.year,
        make: values.make,
        model: values.model,
        vin: values.vin,
        value: String(parseCurrencyToNumber(values.value)),
        bodyType: values.bodyType,
        class: values.class,
        includeAPDTowing: !!values.includeAPDTowing,
        deductibleType: "FollowsPolicy",
        deductible: "2500",
        hitchType: values.hitchType,
      };
      context.notification?.show?.("Vehicle created successfully!", "success", true, "toast", "check-circle");
      onDone(normalized);
    } else {
      if (!initial?.vehicleId) {
        context.notification?.show?.("Missing vehicle id.", "error", true);
        return;
      }
      await updateVehicle(initial.vehicleId, payload);
      const normalized = {
        ...initial,
        year: values.year,
        make: values.make,
        model: values.model,
        vin: values.vin,
        value: String(parseCurrencyToNumber(values.value)),
        bodyType: values.bodyType,
        class: values.class,
        includeAPDTowing: !!values.includeAPDTowing,
        hitchType: values.hitchType,
      };
      context.notification?.show?.("Vehicle updated", "success", true);
      onDone(normalized);
    }

    onOpenChange(false);
    form.reset();
  };

  const getAgeInfo = (values) => {
    const age = new Date().getFullYear() - Number(values.year);
    const isDump = values.bodyType === "dump_truck";
    const maxAge = isDump ? 26 : 23;
    return { age, maxAge, isDump, overLimit: Number.isFinite(age) && age > maxAge };
  };

  const onSubmit = handleSubmit(async (values) => {
    const ageInfo = getAgeInfo(values);
    const overLimit = ageInfo.overLimit;
    const message = ageInfo.isDump
      ? "Dump trucks above 26 years of age are not eligible"
      : "Vehicles above 23 years of age are not eligible";

    if (hasCwOrNirvana && overLimit) {
      if (!hasProgressive) {
        form.setError("year", { type: "custom", message });
        return;
      }
      setPendingValues(values);
      setConfirmMessage(
        `This vehicle is over ${ageInfo.maxAge} years old and not allowed to be quoted by Cover Whale/Nirvana. If you proceed, the carrier(s) will be eliminated from your quote.`
      );
      setConfirmOpen(true);
      return;
    }

    try {
      await performSave(values);
    } catch (err) {
      const msg = err?.response?.data?.message ?? (mode === "create" ? "Failed to create vehicle" : "Failed to update vehicle");
      context.notification?.show?.(msg, "error", true, "toast", "x-circle");
    }
  });

  useEffect(() => {
    if (vin === prevVin) return;
    setPrevVin(vin);

    if (mode === "edit" && initial?.vin && vin === initial.vin) {
      form.setValue("year", initial.year ?? "");
      form.setValue("make", initial.make ?? "");
      form.setValue("model", initial.model ?? "");
      form.setValue("class", initial.class ?? "");
      return;
    }

    if (vin?.length === 17) {
      getVehicleData(vin, values).then((data) => {
        form.setValue("year", data.year);
        form.setValue("make", data.make);
        form.setValue("model", data.model);
        form.setValue("class", data.class_value);
      });
    }
  }, [vin]);

  useEffect(() => {
    if (initial) {
      Object.keys(initial).forEach((k) => form.setValue(k, (initial)[k]));
      setPrevVin(initial.vin);
    }
  }, [initial]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add Vehicle" : "Edit Vehicle"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className={`space-y-6`}>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Year</Label>
              <Controller control={control} name="year" render={({ field, fieldState }) => (
                <>
                  <Input type="number" placeholder="Enter..." {...field} disabled />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Make</Label>
              <Controller control={control} name="make" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} disabled />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Controller control={control} name="model" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} disabled />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>VIN</Label>
              <Controller control={control} name="vin" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." {...field} maxLength={17} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Value</Label>
              <Controller control={control} name="value" render={({ field, fieldState }) => (
                <>
                  <Input placeholder="Enter..." value={field.value ?? ""} onChange={(e) => field.onChange(normalizeCurrencyInput(e.target.value))} />
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Body Type</Label>
              <Controller control={control} name="bodyType" render={({ field, fieldState }) => (
                <>
                  <Select value={field.value ?? ""} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {vehiclesBodytype.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
            <div className="space-y-2">
              <Label>Class</Label>
              <Controller control={control} name="class" render={({ field, fieldState }) => (
                <>
                  <Select value={field.value ?? ""} onValueChange={(v) => {
                    field.onChange(v);
                    if (v === "1" && hasCoverWhale) {
                      context.notification?.show?.(
                        "Sorry at this time Cover Whale does not insure Class 1.",
                        "error",
                        true
                      );
                    }
                  }}>
                    <SelectTrigger className="w-full h-10 overflow-hidden flex">
                      <div className="flex-1 max-w-[80%] overflow-hidden text-ellipsis">
                        <SelectValue placeholder="Select" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {classOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                </>
              )} />
            </div>
          </div>

          {/* NEW: Progressive-only Hitch Type */}
          {hasProgressive && (
            <div className="space-y-2">
              <Label>Hitch Type *</Label>
              <Controller
                control={control}
                name="hitchType"
                render={({ field, fieldState }) => (
                  <>
                    <Select value={field.value ?? ""} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full h-10">
                        <SelectValue placeholder="Select Hitch Type" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        {HITCH_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                  </>
                )}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <Controller control={control} name="includeAPDTowing" render={({ field }) => (
              <Switch checked={!!field.value} onCheckedChange={field.onChange} />
            )} />
            <Label className="cursor-pointer">Include APD/Towing Coverage?</Label>
          </div>

          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={isSubmitting}>Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Proceed with Progressive only?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p>{confirmMessage}</p>
          </div>
          <DialogFooter className="justify-end space-x-2">
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingValues(null); }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                if (!pendingValues) return;
                try {
                  removeCwNirvanaCarriers();
                  await performSave(pendingValues);
                } catch (err) {
                  const msg =
                    err?.response?.data?.message ??
                    (mode === "create" ? "Failed to create vehicle" : "Failed to update vehicle");
                  context.notification?.show?.(msg, "error", true, "toast", "x-circle");
                } finally {
                  setConfirmOpen(false);
                  setPendingValues(null);
                }
              }}
            >
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
